import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Button,
    Typography,
    Link,
    Stack,
    Snackbar,
    Alert,
    Chip,
    Container,
    Popover,
    TextField,
    List,
    ListItem,
    ListItemText,
    Checkbox,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    MenuItem,
    Select,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import FilterListIcon from '@mui/icons-material/FilterList';
import { getAllLeads, updateLead, addLead, createOrUpdateBusiness } from '../../../services/leadService';
import { Lead, LeadStatus } from '../../../models/Lead';
import { SharedTable, Column } from '../../../components/shared/table/Table';
import { StatusChip, ScoreChip } from '../../../components/shared/StyledTable';
import { getIndustries } from '../../../services/industryService';
import Autocomplete from '@mui/material/Autocomplete';
import CircularProgress from '@mui/material/CircularProgress';
import { debounce } from 'lodash';

type Order = 'asc' | 'desc';

interface CityOption {
    id: number;
    name: string;
    region: string;
    country: string;
    formatted: string;
}

const LeadsComponent: React.FC = () => {
    const navigate = useNavigate();
    const [leads, setLeads] = useState<Lead[]>([]);
    const [hotLeads, setHotLeads] = useState<Lead[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [orderBy, setOrderBy] = useState<string>('createdAt');
    const [order, setOrder] = useState<Order>('desc');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [showExportSuccess, setShowExportSuccess] = useState(false);
    const [exportInProgress, setExportInProgress] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedStatuses, setSelectedStatuses] = useState<LeadStatus[]>([]);
    const [selectedLocations, setSelectedLocations] = useState<string[]>([]);
    const [locationFilterAnchor, setLocationFilterAnchor] = useState<null | HTMLElement>(null);
    const [locationSearchQuery, setLocationSearchQuery] = useState('');
    const [addLeadOpen, setAddLeadOpen] = useState(false);
    const [newLead, setNewLead] = useState({
        company: '',
        website: '',
        city: '',
        state: '',
        industryType: '',
        phone: '',
    });
    const [addLeadError, setAddLeadError] = useState<string | null>(null);
    const [addLeadLoading, setAddLeadLoading] = useState(false);
    const [industries, setIndustries] = useState<{ id: string; name: string }[]>([]);
    const [cities, setCities] = useState<CityOption[]>([]);
    const [selectedCity, setSelectedCity] = useState<CityOption | null>(null);
    const [loadingCities, setLoadingCities] = useState(false);
    const citiesCache = React.useRef<Record<string, CityOption[]>>({});

    useEffect(() => {
        loadLeads();
        // Fetch industries for dropdown
        (async () => {
            const inds = await getIndustries();
            setIndustries(inds.map(i => ({ id: i.id, name: i.name })));
        })();
    }, []);

    const loadLeads = async () => {
        try {
            setLoading(true);
            setError(null);
            const fetchedLeads = await getAllLeads();
            setLeads(fetchedLeads);
            setHotLeads(fetchedLeads.filter(lead => lead.status === 'Hot Lead'));
        } catch (err) {
            console.error('Error loading leads:', err);
            setError('Failed to load leads. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (dateString: string | Date) => {
        if (!dateString) return 'N/A';
        const date = typeof dateString === 'string' ? new Date(dateString) : dateString;
        return date.toLocaleDateString(undefined, {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const handleRequestSort = (property: string) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const handleStatusFilterChange = (status: LeadStatus) => {
        setSelectedStatuses(prev => {
            if (prev.includes(status)) {
                return prev.filter(s => s !== status);
            } else {
                return [...prev, status];
            }
        });
        setPage(0); // Reset to first page when filter changes
    };

    const getComparator = (order: Order, orderBy: string) => {
        return order === 'desc'
            ? (a: any, b: any) => descendingComparator(a, b, orderBy)
            : (a: any, b: any) => -descendingComparator(a, b, orderBy);
    };

    const descendingComparator = (a: any, b: any, orderBy: string) => {
        // Special handling for cityState
        if (orderBy === 'cityState') {
            const aLocation = `${a.city || ''}, ${a.state || ''}`.trim();
            const bLocation = `${b.city || ''}, ${b.state || ''}`.trim();
            return aLocation.localeCompare(bLocation);
        }

        // Special handling for dates
        if (orderBy === 'createdAt') {
            return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
        }

        // Special handling for scores
        if (orderBy === 'score') {
            const aScore = a.business?.analysis_result?.score ?? -1;
            const bScore = b.business?.analysis_result?.score ?? -1;
            return bScore - aScore;
        }

        if (orderBy === 'visualScore') {
            const aScore = a.business?.analysis_result?.visual_score ?? -999;
            const bScore = b.business?.analysis_result?.visual_score ?? -999;
            return bScore - aScore;
        }

        // Default string comparison
        if (b[orderBy] < a[orderBy]) return -1;
        if (b[orderBy] > a[orderBy]) return 1;
        return 0;
    };

    const handleExportLeads = async () => {
        try {
            setExportInProgress(true);
            const approvedLeads = leads.filter(lead => lead.status === 'Approved');

            if (approvedLeads.length === 0) {
                setError('No approved leads to export.');
                return;
            }

            const exportData = approvedLeads.map(lead => ({
                company: lead.company || 'N/A',
                phone: lead.phone || 'N/A',
                website: lead.website || 'N/A',
                address: lead.business?.address || 'N/A',
                city: lead.city || 'N/A',
                state: lead.state || 'N/A',
                industryType: lead.industryType || 'N/A',
                score: lead.business?.analysis_result?.score || 'N/A',
                visualScore: lead.business?.analysis_result?.visual_score || 'N/A',
                issues: lead.business?.analysis_result?.issues || [],
                notes: lead.notes?.map(note => ({
                    text: note.text,
                    createdAt: note.createdAt
                })) || [],
                createdAt: lead.createdAt,
                updatedAt: lead.updatedAt
            }));

            const exportBlob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
            const exportUrl = URL.createObjectURL(exportBlob);
            const downloadLink = document.createElement('a');
            downloadLink.href = exportUrl;
            downloadLink.download = `leads-export-${new Date().toISOString().split('T')[0]}.json`;
            document.body.appendChild(downloadLink);
            downloadLink.click();
            document.body.removeChild(downloadLink);
            URL.revokeObjectURL(exportUrl);

            await Promise.all(approvedLeads.map(lead =>
                updateLead(lead.id, { status: 'In Progress' })
            ));

            setLeads(leads.map(lead =>
                lead.status === 'Approved'
                    ? { ...lead, status: 'In Progress' }
                    : lead
            ));

            setShowExportSuccess(true);
        } catch (error) {
            console.error('Error exporting leads:', error);
            setError('Failed to export leads. Please try again.');
        } finally {
            setExportInProgress(false);
        }
    };

    // Get unique locations from all leads
    const availableLocations = useMemo(() => {
        const locations = new Set<string>();
        leads.forEach(lead => {
            if (lead.city && lead.state) {
                locations.add(`${lead.city}, ${lead.state}`);
            }
        });
        return Array.from(locations).sort();
    }, [leads]);

    // Filter locations based on search
    const filteredLocations = useMemo(() => {
        if (!locationSearchQuery) return availableLocations;
        return availableLocations.filter(loc =>
            loc.toLowerCase().includes(locationSearchQuery.toLowerCase())
        );
    }, [availableLocations, locationSearchQuery]);

    const handleLocationFilterClick = (event: React.MouseEvent<HTMLElement>) => {
        setLocationFilterAnchor(event.currentTarget);
    };

    const handleLocationFilterClose = () => {
        setLocationFilterAnchor(null);
    };

    const handleLocationToggle = (location: string) => {
        setSelectedLocations(prev =>
            prev.includes(location)
                ? prev.filter(l => l !== location)
                : [...prev, location]
        );
    };

    const clearLocationFilters = () => {
        setSelectedLocations([]);
    };

    // Update the filtered leads logic
    const filteredLeads = leads.filter(lead => {
        // Exclude In Progress and Complete
        if (lead.status === 'In Progress' || lead.status === 'Complete' || lead.status === 'Rejected') return false;
        // Apply status filter
        if (selectedStatuses.length > 0 && !selectedStatuses.includes(lead.status)) return false;
        // Apply location filter
        if (selectedLocations.length > 0) {
            const leadLocation = lead.city && lead.state ? `${lead.city}, ${lead.state}` : '';
            if (!selectedLocations.includes(leadLocation)) return false;
        }
        // Apply search
        if (searchQuery) {
            const searchLower = searchQuery.toLowerCase();
            return (
                lead.company?.toLowerCase().includes(searchLower) ||
                `${lead.city}, ${lead.state}`.toLowerCase().includes(searchLower) ||
                (lead.industryType || '').toLowerCase().includes(searchLower) ||
                lead.website?.toLowerCase().includes(searchLower) ||
                lead.phone?.toLowerCase().includes(searchLower)
            );
        }
        return true;
    });

    const sortedData = [...filteredLeads].sort(getComparator(order, orderBy));
    const paginatedData = sortedData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    // Define columns for Hot Leads table (no score/visual score)
    const hotLeadColumns: Column<Lead>[] = [
        {
            id: 'createdAt',
            label: 'Created',
            width: 180,
            sortable: true,
            render: (row) => formatDate(row.createdAt)
        },
        {
            id: 'company',
            label: 'Company',
            width: 300,
            render: (row) => (
                <Box>
                    <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 500 }}>
                        {row.company || 'N/A'}
                    </Typography>
                    {row.website && (
                        <Typography variant="body2" color="primary" sx={{ fontSize: '0.75rem' }}>
                            <Link href={row.website} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>
                                {row.website}
                            </Link>
                        </Typography>
                    )}
                </Box>
            )
        },
        {
            id: 'cityState',
            label: 'Location',
            width: 150,
            sortable: true,
            render: (row) => (row.city && row.state ? `${row.city}, ${row.state}` : 'N/A')
        },
        {
            id: 'industryType',
            label: 'Industry',
            width: 150,
            render: (row) => row.industryType || 'N/A'
        },
        {
            id: 'status',
            label: 'Status',
            width: 120,
            render: (row) => (
                <StatusChip
                    label={row.status}
                    status={row.status}
                    size="small"
                    color={row.status === 'Hot Lead' ? 'error' : 'primary'}
                    sx={row.status === 'Hot Lead' ? { backgroundColor: '#ff1744', color: '#fff', fontWeight: 700 } : {}}
                />
            )
        }
    ];

    const statusOptions: LeadStatus[] = ['new', 'In Review', 'Approved'];

    // Debounced function to fetch cities
    const fetchCities = debounce(async (query: string) => {
        if (!query || query.length < 2) {
            setCities([]);
            return;
        }
        if (citiesCache.current[query]) {
            setCities(citiesCache.current[query]);
            return;
        }
        setLoadingCities(true);
        try {
            const response = await fetch(
                `https://wft-geo-db.p.rapidapi.com/v1/geo/cities?namePrefix=${query}&limit=10&sort=-population&countryIds=US`,
                {
                    method: 'GET',
                    headers: {
                        'X-RapidAPI-Key': process.env.REACT_APP_RAPIDAPI_KEY || '',
                        'X-RapidAPI-Host': 'wft-geo-db.p.rapidapi.com'
                    }
                }
            );
            if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
            const data = await response.json();
            if (data && Array.isArray(data.data)) {
                const formattedCities: CityOption[] = data.data.map((city: any) => ({
                    id: city.id,
                    name: city.city,
                    region: city.region,
                    country: city.country,
                    formatted: `${city.city}, ${city.region}`
                }));
                citiesCache.current[query] = formattedCities;
                setCities(formattedCities);
            } else {
                setCities([]);
            }
        } catch (error) {
            setCities([]);
        } finally {
            setLoadingCities(false);
        }
    }, 800);

    // Rename columns for main leads table
    const mainColumns: Column<Lead>[] = [
        {
            id: 'createdAt',
            label: 'Created',
            width: 180,
            sortable: true,
            render: (row) => formatDate(row.createdAt)
        },
        {
            id: 'company',
            label: 'Company',
            width: 300,
            render: (row) => (
                <Box>
                    <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 500 }}>
                        {row.company || 'N/A'}
                    </Typography>
                    {row.website && (
                        <Typography variant="body2" color="primary" sx={{ fontSize: '0.75rem' }}>
                            <Link href={row.website} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>
                                {row.website}
                            </Link>
                        </Typography>
                    )}
                </Box>
            )
        },
        {
            id: 'cityState',
            label: 'Location',
            width: 150,
            sortable: true,
            render: (row) => (row.city && row.state ? `${row.city}, ${row.state}` : 'N/A')
        },
        {
            id: 'industryType',
            label: 'Industry',
            width: 150,
            render: (row) => row.industryType || 'N/A'
        },
        {
            id: 'score',
            label: 'Score',
            width: 100,
            numeric: true,
            sortable: true,
            render: (row) => (
                row.business?.analysis_result?.score !== undefined && (
                    <ScoreChip
                        label={`${row.business.analysis_result.score}/100`}
                        score={row.business.analysis_result.score}
                        size="small"
                    />
                )
            )
        },
        {
            id: 'visualScore',
            label: 'Visual Score',
            width: 100,
            numeric: true,
            sortable: true,
            render: (row) => (
                row.business?.analysis_result?.visual_score !== undefined && (
                    <ScoreChip
                        label={row.business.analysis_result.visual_score}
                        score={row.business.analysis_result.visual_score >= -20 ? 60 : 40}
                        size="small"
                    />
                )
            )
        },
        {
            id: 'status',
            label: 'Status',
            width: 120,
            render: (row) => (
                <StatusChip
                    label={row.status}
                    status={row.status}
                    size="small"
                />
            )
        }
    ];

    return (
        <Container maxWidth="lg">
            <Box sx={{ py: 4 }}>
                <Typography variant="h4" component="h1" gutterBottom>
                    Lead Management
                </Typography>

                {/* Hot Leads Table - moved above main table */}
                {hotLeads.length > 0 && (
                    <Box sx={{ mb: 4 }}>
                        <Typography variant="h5" sx={{ mb: 2 }}>Hot Leads</Typography>
                        <SharedTable
                            columns={hotLeadColumns}
                            data={hotLeads}
                            loading={loading}
                            error={error}
                            orderBy={orderBy}
                            order={order}
                            page={0}
                            rowsPerPage={hotLeads.length}
                            totalCount={hotLeads.length}
                            searchQuery={searchQuery}
                            searchPlaceholder="Search Hot Leads..."
                            onSearchChange={setSearchQuery}
                            onRequestSort={handleRequestSort}
                            onPageChange={() => { }}
                            onRowsPerPageChange={() => { }}
                            onRowClick={(row) => navigate(`/leads/lead/${row.id}`)}
                            getRowId={(row) => row.id}
                            emptyStateMessage="No Hot Leads found."
                            headerContent={null}
                        />
                    </Box>
                )}

                <Stack direction="row" spacing={1} sx={{ mb: 3 }}>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={() => setAddLeadOpen(true)}
                        sx={{ textTransform: 'none' }}
                    >
                        Add Hot Lead
                    </Button>
                    <Button
                        variant="outlined"
                        startIcon={<FilterListIcon />}
                        onClick={handleLocationFilterClick}
                        color={selectedLocations.length > 0 ? "primary" : "inherit"}
                    >
                        Locations ({selectedLocations.length})
                    </Button>
                    {statusOptions.map((status) => (
                        <Chip
                            key={status}
                            label={status}
                            color={selectedStatuses.includes(status) ? 'primary' : 'default'}
                            onClick={() => handleStatusFilterChange(status)}
                            variant={selectedStatuses.includes(status) ? 'filled' : 'outlined'}
                            sx={{ textTransform: 'capitalize' }}
                        />
                    ))}
                </Stack>

                <Popover
                    open={Boolean(locationFilterAnchor)}
                    anchorEl={locationFilterAnchor}
                    onClose={handleLocationFilterClose}
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'left',
                    }}
                    transformOrigin={{
                        vertical: 'top',
                        horizontal: 'left',
                    }}
                >
                    <Box sx={{ p: 2, width: 300 }}>
                        <Stack spacing={2}>
                            <TextField
                                size="small"
                                fullWidth
                                placeholder="Search locations..."
                                value={locationSearchQuery}
                                onChange={(e) => setLocationSearchQuery(e.target.value)}
                            />
                            <List sx={{ maxHeight: 300, overflow: 'auto' }}>
                                {filteredLocations.map((location) => (
                                    <ListItem
                                        key={location}
                                        dense
                                        button
                                        onClick={() => handleLocationToggle(location)}
                                    >
                                        <Checkbox
                                            edge="start"
                                            checked={selectedLocations.includes(location)}
                                            tabIndex={-1}
                                            disableRipple
                                        />
                                        <ListItemText primary={location} />
                                    </ListItem>
                                ))}
                            </List>
                            {selectedLocations.length > 0 && (
                                <Button
                                    variant="text"
                                    onClick={clearLocationFilters}
                                    sx={{ alignSelf: 'flex-end' }}
                                >
                                    Clear All
                                </Button>
                            )}
                        </Stack>
                    </Box>
                </Popover>

                <Dialog open={addLeadOpen} onClose={() => setAddLeadOpen(false)} maxWidth="sm" fullWidth>
                    <DialogTitle>Add New Hot Lead</DialogTitle>
                    <DialogContent>
                        <Stack spacing={2} sx={{ mt: 1 }}>
                            <TextField
                                label="Company"
                                value={newLead.company}
                                onChange={e => setNewLead({ ...newLead, company: e.target.value })}
                                required
                                fullWidth
                            />
                            <TextField
                                label="Website"
                                value={newLead.website}
                                onChange={e => setNewLead({ ...newLead, website: e.target.value })}
                                fullWidth
                            />
                            <Autocomplete
                                fullWidth
                                options={cities}
                                getOptionLabel={(option) => option.formatted}
                                loading={loadingCities}
                                value={selectedCity}
                                onChange={(_, value) => {
                                    setSelectedCity(value);
                                    setNewLead({ ...newLead, city: value ? value.name : '', state: value ? value.region : '' });
                                }}
                                onInputChange={(_, value) => fetchCities(value)}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="City"
                                        placeholder="e.g., Austin, TX"
                                        InputProps={{
                                            ...params.InputProps,
                                            endAdornment: (
                                                <>
                                                    {loadingCities ? <CircularProgress color="inherit" size={20} /> : null}
                                                    {params.InputProps.endAdornment}
                                                </>
                                            ),
                                        }}
                                    />
                                )}
                            />
                            <Autocomplete
                                fullWidth
                                options={industries}
                                getOptionLabel={(option) => option.name}
                                value={industries.find(i => i.name === newLead.industryType) || null}
                                onChange={(_, value) => setNewLead({ ...newLead, industryType: value ? value.name : '' })}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Industry Type"
                                        required
                                        placeholder="Select Industry"
                                    />
                                )}
                            />
                            <TextField
                                label="Phone"
                                value={newLead.phone}
                                onChange={e => setNewLead({ ...newLead, phone: e.target.value })}
                                fullWidth
                            />
                            {addLeadError && <Alert severity="error">{addLeadError}</Alert>}
                        </Stack>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setAddLeadOpen(false)} disabled={addLeadLoading}>Cancel</Button>
                        <Button
                            variant="contained"
                            color="primary"
                            disabled={addLeadLoading}
                            onClick={async () => {
                                setAddLeadError(null);
                                if (!newLead.company || !newLead.city || !newLead.state || !newLead.industryType) {
                                    setAddLeadError('Please fill in all required fields.');
                                    return;
                                }
                                setAddLeadLoading(true);
                                try {
                                    // Construct business id as before
                                    const businessId = `${newLead.company.replace(/\s+/g, '_').toLowerCase()}_${Date.now()}`;
                                    // Set createdAt for both lead and business
                                    const createdAt = new Date();
                                    // Prepare business object for manual creation
                                    await createOrUpdateBusiness(businessId, {
                                        id: businessId,
                                        name: newLead.company,
                                        address: '',
                                        city: newLead.city,
                                        state: newLead.state,
                                        phone: newLead.phone,
                                        website: newLead.website,
                                        industryType: newLead.industryType,
                                        has_website: !!newLead.website,
                                        website_analyzed: false,
                                        createdAt,
                                        updatedAt: createdAt,
                                        createdVia: 'manual',
                                        searchHistoryIds: [],
                                    });
                                    // Create the Hot Lead in the database
                                    const leadToAdd = {
                                        ...newLead,
                                        status: 'Hot Lead' as LeadStatus,
                                        source: 'manual' as const,
                                        businessId,
                                        analyzerModel: null,
                                        createdAt,
                                        updatedAt: createdAt,
                                    };
                                    const newLeadId = await addLead(leadToAdd);
                                    // Reload leads
                                    await loadLeads();
                                    setAddLeadOpen(false);
                                    setNewLead({ company: '', website: '', city: '', state: '', industryType: '', phone: '' });
                                    // Navigate to the new lead's details page
                                    navigate(`/leads/lead/${newLeadId}`);
                                } catch (err) {
                                    setAddLeadError('Failed to add Hot Lead.');
                                } finally {
                                    setAddLeadLoading(false);
                                }
                            }}
                        >
                            Add Hot Lead
                        </Button>
                    </DialogActions>
                </Dialog>

                <SharedTable
                    columns={mainColumns}
                    data={paginatedData}
                    loading={loading}
                    error={error}
                    orderBy={orderBy}
                    order={order}
                    page={page}
                    rowsPerPage={rowsPerPage}
                    totalCount={filteredLeads.length}
                    searchQuery={searchQuery}
                    searchPlaceholder="Search by company, location, industry..."
                    onSearchChange={setSearchQuery}
                    onRequestSort={handleRequestSort}
                    onPageChange={setPage}
                    onRowsPerPageChange={setRowsPerPage}
                    onRowClick={(row) => navigate(`/leads/lead/${row.id}`)}
                    getRowId={(row) => row.id}
                    emptyStateMessage="No leads found. Start by searching for businesses to add leads."
                    headerContent={
                        <Button
                            variant="contained"
                            color="primary"
                            startIcon={<DownloadIcon />}
                            onClick={handleExportLeads}
                            disabled={exportInProgress || !leads.some(lead => lead.status === 'Approved')}
                            sx={{ textTransform: 'none' }}
                        >
                            Export Approved Leads
                        </Button>
                    }
                />

                <Snackbar
                    open={showExportSuccess}
                    autoHideDuration={3000}
                    onClose={() => setShowExportSuccess(false)}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                >
                    <Alert onClose={() => setShowExportSuccess(false)} severity="success" sx={{ width: '100%' }}>
                        Leads exported successfully and marked as In Progress!
                    </Alert>
                </Snackbar>
            </Box>
        </Container>
    );
};

export default LeadsComponent; 